import type React from 'react';
import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '../../../components/Button';
import type { OpportunityList } from '../../../domain/list';
import { OpportunityListTypeEnum } from '../../../domain/list';
import useFocusOnMount from '../../../hooks/useFocusOnMount';

import {
  StyledCreateNewListText,
  StyledCreateNewListTextInput,
  StyledCreateNewListWrapper,
  StyledCreateNewListModalFooter,
} from './styles';

export type CreateNewListSectionProps = {
  domain: string;
  onClose(): void;
  onSaveToList(list: OpportunityList): void;
};

export const CreateNewListSection = ({ domain, onClose, onSaveToList }: CreateNewListSectionProps) => {
  const { t } = useTranslation();
  const [newListName, setNewListName] = useState('');

  const inputRef = useFocusOnMount();

  const isNameLongEnough = newListName.trim().length >= 1;

  const handleCloseCreateMode = () => {
    onClose();
    setNewListName('');
  };

  const handleChangeNewListName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewListName(e.target.value);
  };

  const handleConfirmCreateNewList = () => {
    handleCloseCreateMode();
    onSaveToList({ id: '', name: newListName, domains: [domain], type: OpportunityListTypeEnum.ManuallyCreated });
  };

  return (
    <StyledCreateNewListWrapper data-automation="CreateNewWrapper">
      <StyledCreateNewListText data-automation="CreateNewText">
        {t('company.save_to_list.new_list.label')}
      </StyledCreateNewListText>
      <StyledCreateNewListTextInput
        onChange={handleChangeNewListName}
        placeholder={t('company.save_to_list.new_list.placeholder') || ''}
        data-automation="CreateNewInput"
        ref={inputRef}
      />
      <StyledCreateNewListModalFooter data-automation="CreateNewModalFooter">
        <Button onClick={handleCloseCreateMode} variant="flat" data-automation="CreateNewCancel">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConfirmCreateNewList}
          variant="primary"
          disabled={!isNameLongEnough}
          data-automation="CreateNewButton"
        >
          {t('company.save_to_list.new_list.create.confirm')}
        </Button>
      </StyledCreateNewListModalFooter>
    </StyledCreateNewListWrapper>
  );
};
